import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, GripVertical, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const PRESET_COLORS = ["#3b82f6", "#f59e0b", "#10b981", "#8b5cf6", "#06b6d4", "#22c55e", "#ef4444", "#ec4899", "#71717a", "#f97316", "#a855f7"];

function SortableRow({ status, onEdit, onDelete, canManage }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: status.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 p-3 bg-card border border-border rounded-md" data-testid={`svc-status-row-${status.name}`}>
      {canManage && (
        <button {...attributes} {...listeners} className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing">
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <span className="h-3 w-3 rounded-full shrink-0" style={{ background: status.color }} />
      <span className="text-sm font-medium flex-1">{status.name}</span>
      {status.is_default && <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Implicit</span>}
      {status.is_terminal && <span className="text-[10px] uppercase tracking-widest text-amber-500 font-semibold">Terminal</span>}
      {canManage && (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(status)}><Pencil className="h-3.5 w-3.5" /></Button>
          {!status.is_default && (
            <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-500" onClick={() => onDelete(status)}><Trash2 className="h-3.5 w-3.5" /></Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ServiceStatusesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_service_settings");
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: PRESET_COLORS[0], is_terminal: false });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    try {
      const { data } = await api.get("/service/statuses");
      setStatuses(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", color: PRESET_COLORS[0], is_terminal: false }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, color: s.color, is_terminal: !!s.is_terminal }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nume necesar");
    try {
      if (editing) await api.patch(`/service/statuses/${editing.id}`, form);
      else await api.post("/service/statuses", { ...form, order: statuses.length });
      toast.success(editing ? "Status actualizat" : "Status creat");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (s) => {
    if (!confirm(`Ștergi statusul "${s.name}"?`)) return;
    try {
      await api.delete(`/service/statuses/${s.id}`);
      toast.success("Status șters");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = statuses.findIndex((s) => s.id === active.id);
    const newIdx = statuses.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(statuses, oldIdx, newIdx);
    setStatuses(reordered);
    try { await api.post("/service/statuses/reorder", { order: reordered.map((s) => s.id) }); }
    catch { toast.error("Reordonare eșuată"); load(); }
  };

  return (
    <div className="space-y-6" data-testid="service-statuses-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Setări · Service</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Statusuri service</h1>
          <p className="text-muted-foreground text-sm mt-1">Configurează workflow-ul cererilor de service & garanție.</p>
        </div>
        {canManage && <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1.5" /> Status nou</Button>}
      </div>

      <Card className="p-4 bg-card border-border">
        {loading ? (
          <div className="text-sm text-muted-foreground p-4">Se încarcă…</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={statuses.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {statuses.map((s) => (<SortableRow key={s.id} status={s} onEdit={openEdit} onDelete={del} canManage={canManage} />))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editează status" : "Status nou"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nume</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: În reparație" />
            </div>
            <div className="space-y-2">
              <Label>Culoare</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                    className={`h-8 w-8 rounded-md border-2 transition-all ${form.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="terminal" className="cursor-pointer">Status final (închide cererea)</Label>
              <Switch id="terminal" checked={form.is_terminal} onCheckedChange={(v) => setForm({ ...form, is_terminal: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={save}>{editing ? "Salvează" : "Creează"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
